var m = require('mithril');
var moment = require('moment-timezone');
var headingElement = require('../../components/heading');
var resource = require('../../network/models/resource');
var Point = require('../models/point');
var buttonElement = require('../../components/button');
var suppressionDurationModal = require('./suppressionDurationModal');
var pollGroupSampling = require('./pollGroupSampling');
var loaders = require('../../layout/components/loaders');
var alerts = require('../../layout/components/alerts');

var samplingIntervalOptions = [
  {label: 'No sampling', value: 0},
  {label: '1 minute', value: 60},
  {label: '5 minutes', value: 300},
  {label: '10 minutes', value: 600},
  {label: '15 minutes', value: 900},
  {label: '20 minutes', value: 1200},
  {label: '60 minutes', value: 3600}
];

function samplingIntervalSelection (value, customIntervalSelected) {
  if (customIntervalSelected) return 'custom';
  var numericValue = Number(value);
  var preset = samplingIntervalOptions.find(function (option) {
    return option.value === numericValue;
  });
  return preset ? String(preset.value) : 'custom';
}

function normalizeSuppressionValue (value) {
  if (value === null || typeof value === 'undefined') {
    return 0;
  }
  if (typeof value === 'string') {
    var lower = value.toLowerCase();
    if (lower === 'true') return 1;
    if (lower === 'false') return 0;
    var numFromString = Number(value);
    if (!Number.isNaN(numFromString)) {
      return numFromString === 0 ? 0 : 1;
    }
    return 0;
  }
  if (value === true) return 1;
  if (value === false) return 0;
  var num = Number(value);
  if (Number.isNaN(num)) {
    return 0;
  }
  return num === 0 ? 0 : 1;
}

function normalizeMuteExpiration (value) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (value instanceof Date && typeof value.getTime === 'function') {
    return value.getTime();
  }
  if (typeof value === 'string') {
    var trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (/^\d+$/.test(trimmed)) {
      var asNumber = Number(trimmed);
      return Number.isFinite(asNumber) ? asNumber : null;
    }
    var parsed = Date.parse(trimmed);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    var fallback = Number(trimmed);
    return Number.isFinite(fallback) ? fallback : null;
  }
  return null;
}

function suppressionExpirationMeta (expiresAt) {
  var ts = normalizeMuteExpiration(expiresAt);
  if (!ts) {
    return {label: 'Indefinite', title: 'Indefinite', indefinite: true, expired: false};
  }
  var formatted = moment(ts).format('MM-DD-YYYY h:mm a');
  var isExpired = ts <= Date.now();
  return {
    label: formatted,
    title: formatted,
    indefinite: false,
    expired: isExpired
  };
}

function currentSuppressionExpiresAt () {
  var rawValue = typeof resource.form.suppressAuditNotificationsExpiresAt === 'function'
    ? resource.form.suppressAuditNotificationsExpiresAt()
    : resource.form.suppressAuditNotificationsExpiresAt;
  return normalizeMuteExpiration(rawValue);
}

function samplingIntervalWarningActive (groupSamplingDetails) {
  if (groupSamplingDetails) return false;
  var enabled = Number(Point.form.alarmEnabled && Point.form.alarmEnabled()) === 1;
  var intervalRaw = typeof resource.form.interval === 'function' ? resource.form.interval() : resource.form.interval;
  var intervalNum = Number(intervalRaw);
  var intervalMissing = !Number.isFinite(intervalNum) || intervalNum <= 0;
  return enabled && intervalMissing;
}

function setSuppression (nextValue, expiresAt) {
  var normalized = normalizeSuppressionValue(nextValue);
  resource.form.suppressAuditNotifications(normalized);
  if (typeof resource.form.disableAdminAuditNotifications === 'function') {
    resource.form.disableAdminAuditNotifications(normalized);
  }
  if (typeof resource.form.disableReceiverAuditNotifications === 'function') {
    resource.form.disableReceiverAuditNotifications(normalized);
  }
  if (typeof resource.form.suppressAuditNotificationsExpiresAt === 'function') {
    resource.form.suppressAuditNotificationsExpiresAt(normalized === 1 ? normalizeMuteExpiration(expiresAt) : null);
  }
}

function persistSuppression (nextValue, expiresAt) {
  var rid = resource.record && resource.record['@rid'] ? resource.record['@rid'].toString() : null;
  var normalizedExpiresAt = normalizeMuteExpiration(expiresAt);
  var ridForUrl = rid && rid.charAt(0) === '#' ? rid.slice(1) : rid;

  // If we do not have a persisted record yet, just update the form state.
  if (!ridForUrl) {
    setSuppression(nextValue, normalizedExpiresAt);
    m.redraw();
    return Promise.resolve();
  }

  resource._savingSuppression = true;

  var body = {
    suppressAuditNotifications: nextValue,
    disableAdminAuditNotifications: nextValue,
    disableReceiverAuditNotifications: nextValue,
    suppressAuditNotificationsExpiresAt: normalizedExpiresAt || null
  };

  return loaders.requestWithLoader({
    url: '/api/v1/resource/' + ridForUrl,
    method: 'PUT',
    body: body,
    loader: 'background'
  })
  .then(function () {
    setSuppression(nextValue, normalizedExpiresAt);
    if (resource.record) {
      resource.record.suppressAuditNotifications = nextValue;
      resource.record.disableAdminAuditNotifications = nextValue;
      resource.record.disableReceiverAuditNotifications = nextValue;
      resource.record.suppressAuditNotificationsExpiresAt = normalizedExpiresAt;
    }
  })
  .catch(function (error) {
    var message = (error && error.message) || (error && error.error) || 'Failed to update audit notification preference';
    alerts.vm.add({detail: message});
  })
  .then(function () {
    resource._savingSuppression = false;
    m.redraw();
  });
}

function openSuppressionEditor () {
  var suppressed = normalizeSuppressionValue(resource.form.suppressAuditNotifications()) === 1;
  suppressionDurationModal.open({
    resource: {
      suppressAuditNotifications: suppressed ? 1 : 0
    },
    expiresAt: currentSuppressionExpiresAt(),
    onSave: function (nextExpiry) {
      return persistSuppression(1, nextExpiry);
    },
    onDisable: function () {
      return persistSuppression(0, null);
    }
  });
}

function toggleSuppression () {
  var suppressed = normalizeSuppressionValue(resource.form.suppressAuditNotifications()) === 1;
  if (resource._savingSuppression) {
    return;
  }
  if (!suppressed) {
    openSuppressionEditor();
    return;
  }
  persistSuppression(0, null);
}

function suppressionToggleButton (suppressed, expiresAt, saving, clickHandler) {
  var meta = suppressionExpirationMeta(expiresAt);
  var labelText = saving ? 'Saving…' : (suppressed ? 'Muted' : 'Mute');
  if (suppressed) {
    if (meta.indefinite) {
      labelText = 'Muted indefinitely';
    } else if (meta.expired) {
      labelText = 'Muted (expired ' + meta.label + ')';
    } else if (meta.label) {
      labelText = 'Until ' + meta.label;
    }
  }
  var baseClass = 'w-100 flex items-center justify-center pv2 pointer ba bw1';
  var colorClass = suppressed ? 'bg-gold near-black b--gold' : 'bg-transparent near-white b--gray';
  var opacityClass = saving ? ' o-60' : '';
  return m('button', {
    type: 'button',
    class: baseClass + ' ' + colorClass + opacityClass,
    onclick: clickHandler,
    disabled: saving,
    title: suppressed
      ? (meta.indefinite ? 'Muted indefinitely' : 'Muted until ' + (meta.label || 'unknown time'))
      : 'Mute audit notifications for this resource'
  }, (function () {
    if (saving) return m('span.near-white', 'Saving…');
    return suppressed ? m('span.tc.nowrap.near-black', labelText) : m('i.ion-volume-mute.f3.near-white');
  })());
}

function pollGroupSamplingView (details) {
  var multipleGroups = details.groups.length > 1;
  return m('div.mb3.pa3.ba.b--gray', [
    m('p.mt0', 'This resource\'s sampling interval is dictated by its parent poll ' +
      (multipleGroups ? 'groups.' : 'group.')),
    details.groups.map(function (group) {
      return m('div.mb2', [
        m('div', [m('strong', 'Poll Group: '), group.name]),
        m('div', [m('strong', 'Sampling Interval: '), pollGroupSampling.formatInterval(group.interval)])
      ]);
    }),
    details.editor.href
      ? m(m.route.Link, {href: details.editor.href, class: 'blue link'}, details.editor.label)
      : null
  ]);
}

module.exports = {
  view: function (vnode) {
    var suppressedValue = normalizeSuppressionValue(resource.form.suppressAuditNotifications());
    var expiresAt = currentSuppressionExpiresAt();
    var groupSamplingDetails = pollGroupSampling.details(resource.record);

    return m('div', [
    (function () {
      var labelContent = ['Sampling Interval'];
      if (samplingIntervalWarningActive(groupSamplingDetails)) {
        labelContent.push(
          m('span.ml2.yellow', [
            m('span.mr1', '⚠️'),
            m('span', 'Set a sampling interval to ensure the alarm will be polled for changes')
          ])
        );
      }
      return m(headingElement, {label: labelContent});
    })(),
    m("form", [
      groupSamplingDetails ? pollGroupSamplingView(groupSamplingDetails) : m('div.mb2', [
        m('select.form-control', {
          id: 'sampling-interval',
          value: samplingIntervalSelection(resource.form.interval(), vnode.state.customIntervalSelected),
          onchange: function (ev) {
            vnode.state.customIntervalSelected = ev.target.value === 'custom';
            resource.form.interval(vnode.state.customIntervalSelected ? '' : ev.target.value);
          }
        }, samplingIntervalOptions.map(function (option) {
          return m('option', {value: option.value}, option.label);
        }).concat([
          m('option', {value: 'custom'}, 'Custom interval')
        ])),
        samplingIntervalSelection(resource.form.interval(), vnode.state.customIntervalSelected) === 'custom'
          ? m('div.mt2', [
            m('label.db.mb1', {for: 'sampling-custom-interval'}, 'Custom Interval (Seconds)'),
            m('input.form-control', {
              id: 'sampling-custom-interval',
              type: 'number',
              min: 5,
              step: 1,
              required: true,
              value: resource.form.interval(),
              oninput: function (ev) { resource.form.interval(ev.target.value) }
            }),
            m('small.db.mt1', 'Minimum: 5 seconds')
          ])
          : null
      ]),
      m('div.mb3', [
        m('label.db.mb1', {for: 'audit-notification-suppression'}, 'Mute Audit Notifications'),
        suppressionToggleButton(suppressedValue === 1, expiresAt, resource._savingSuppression, toggleSuppression)
      ]),
      resource.errorView(),
      groupSamplingDetails ? null : m('fr', [
        m(buttonElement, {
          label: 'Submit',
          func: resource.save.bind(resource),
          class: 'w4 fr'
        })
      ])
    ])
  ]);
  }
}
